import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import { useRoomNames } from './useRoomNames';
import { toast } from 'react-toastify';

// All Icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  DialogContentText,
  Drawer,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePrinterRouting } from './printerService';

// ─────────────────────────────────────────────
// RBAC Helper — plain util, NOT a hook.
// Using a regular function avoids the
// react-hooks/rules-of-hooks ESLint error
// that fires when hook names start with "use".
// ─────────────────────────────────────────────
const hasPermission = (code) => {
  try {
    const codes = JSON.parse(sessionStorage.getItem('permissionCodes') || '[]');
    return codes.some((p) => p.code === code);
  } catch {
    return false;
  }
};

// ─────────────────────────────────────────────
// Drawer constants & theme
// ─────────────────────────────────────────────
const ORDER_DRAWER_WIDTH = { xs: '100%', sm: 420, md: 460 };
const ORDER_DRAWER_Z = 1400;

const drawerTheme = {
  primary: '#1565c0',
  bg: '#f0f4f8',
  surface: '#fff',
  border: '#e3eaf3',
  divider: '#e8edf3',
  textMuted: 'rgba(0,0,0,0.55)',
};

function DrawerSection({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color: drawerTheme.primary,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          mb: 0.75,
        }}>
        {title}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${drawerTheme.border}`,
          borderRadius: 2,
          bgcolor: drawerTheme.surface,
          overflow: 'hidden',
        }}>
        {children}
      </Paper>
    </Box>
  );
}

function DrawerRow({ label, value, highlight, isLast }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1.75,
        py: 1.1,
        minHeight: 40,
        bgcolor: highlight ? '#f0f7ff' : 'transparent',
        borderBottom: isLast ? 'none' : `1px solid ${drawerTheme.divider}`,
      }}>
      <Typography
        sx={{ fontSize: 12.5, color: drawerTheme.textMuted, minWidth: 0 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: highlight ? 13.5 : 12.5,
          fontWeight: highlight ? 700 : 600,
          color: highlight ? drawerTheme.primary : 'text.primary',
          textAlign: 'right',
          flexShrink: 0,
        }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function OrderList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { roomLabel } = useRoomNames();

  // ── Permission flags ──────────────────────────────────────────
  // PAGE_ORDERS  → page-level access (always present for this route)
  // ORDER_VIEW   → optional finer-grained read permission
  // Either is enough to view the page.
  const canViewOrders =
    hasPermission('PAGE_ORDERS') || hasPermission('ORDER_VIEW');
  const canPrintBill = hasPermission('ORDER_PRINT_BILL');
  const canApplyDiscount = hasPermission('ORDER_DISCOUNT');

  // ── State ─────────────────────────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [searchText, setSearchText] = useState('');
  const [roomFilter, setRoomFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('All'); // All | Paid | Unpaid
  const [typeFilter, setTypeFilter] = useState('All'); // All | Dine-in | Takeaway | Room
  const [tableFilter, setTableFilter] = useState('');

  // Change-room (fix a room-service order placed on the wrong room).
  const [changeRoomOpen, setChangeRoomOpen] = useState(false);
  const [orderForRoomChange, setOrderForRoomChange] = useState(null);
  const [roomTarget, setRoomTarget] = useState('');
  const [activeRooms, setActiveRooms] = useState([]);
  const [savingRoom, setSavingRoom] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState([]);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  // Manage actions dialog — the details drawer is now detail-only; the order
  // actions live here, opened from the orders table.
  const [manageOpen, setManageOpen] = useState(false);
  // Centralised printer routing (bill printer for Print Bill).
  const { resolveBillPrinter } = usePrinterRouting();

  // Modal States
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);

  const [currentOrderForAction, setCurrentOrderForAction] = useState(null);

  // Form States
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');

  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const Usersdetails = JSON.parse(sessionStorage.getItem('user') || '{}');
  const Companycurrency = company?.currency ?? '₹';
  const token = sessionStorage.getItem('token');

  const getCurrency = (symbol) => {
    switch (symbol) {
      case '₹':
        return 'Rs.';
      case '$':
        return '$';
      case '€':
        return 'EUR';
      default:
        return symbol;
    }
  };
  const currency = getCurrency(Companycurrency);

  // ── Fetch Orders ──────────────────────────────────────────────
  const fetchOrders = async () => {
    if (!company?.id) {
      setError('Company not found. Please login again.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/Getallaorders/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!res.ok) throw new Error('Failed to fetch orders');
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
      console.log('Orderdata', data);
    } catch (err) {
      setError(err.message || 'Failed to fetch orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Change Room for a room-service order (fix wrong room) ──
  const openChangeRoom = async (order) => {
    setOrderForRoomChange(order);
    setRoomTarget('');
    setChangeRoomOpen(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/Getallactivebookings/${company?.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      const rooms = [];
      list.forEach((b) => {
        const guest = `${b.firstName || ''} ${b.lastName || ''}`.trim();
        (Array.isArray(b.bookingRoomDetails) ? b.bookingRoomDetails : []).forEach(
          (rd) => {
            const rn = rd.roomnumber ?? rd.roomNumber;
            if (rn != null && rn !== '')
              rooms.push({
                room: String(rn),
                label: guest ? `Room ${rn} — ${guest}` : `Room ${rn}`,
              });
          },
        );
      });
      const seen = new Set();
      setActiveRooms(
        rooms.filter((r) => (seen.has(r.room) ? false : seen.add(r.room))),
      );
    } catch {
      setActiveRooms([]);
    }
  };

  const submitChangeRoom = async () => {
    if (!orderForRoomChange) return;
    if (!roomTarget) return;
    const uid =
      JSON.parse(sessionStorage.getItem('user') || '{}')?.id || 0;
    setSavingRoom(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/restaurantorder/changeroomorder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            orderId: orderForRoomChange.id,
            newRoomNo: Number(roomTarget),
            companyId: company?.id,
            changedBy: uid,
            changedByName:
              JSON.parse(sessionStorage.getItem('user') || '{}')?.name || '',
          }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        toast.error(body?.error || body?.message || 'Failed to change room');
        return;
      }
      toast.success(body?.message || 'Order moved to the correct room!');
      setChangeRoomOpen(false);
      fetchOrders();
    } catch {
      toast.error('Failed to change room');
    } finally {
      setSavingRoom(false);
    }
  };

  useEffect(() => {
    // Only fetch if user has view permission
    if (canViewOrders) {
      fetchOrders();
    }
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredOrders = orders.filter((o) => {
    const nameMatch = (o?.firstName || '')
      .toLowerCase()
      .includes(searchText.toLowerCase());
    const roomMatch =
      !roomFilter || (o?.roomNumbers || '').includes(roomFilter);
    const orderDate = new Date(o.createdate);
    const fromMatch = !fromDate || orderDate >= new Date(fromDate);
    const toMatch = !toDate || orderDate <= new Date(toDate + 'T23:59:59');

    const statusMatch =
      statusFilter === 'All' ||
      (statusFilter === 'Paid' ? !!o.isPaid : !o.isPaid);

    const table = (o?.tableno || '').toString();
    const typeMatch =
      typeFilter === 'All' ||
      (typeFilter === 'Takeaway'
        ? table === 'Takeaway'
        : typeFilter === 'Room'
          ? (o?.bookingId || 0) > 0 || !!(o?.roomNumbers || '').trim()
          : // Dine-in: has a real table that isn't Takeaway, and isn't a room order
            table && table !== 'Takeaway' && (o?.bookingId || 0) === 0);

    const tableMatch =
      !tableFilter ||
      table.toLowerCase().includes(tableFilter.toLowerCase());

    return (
      nameMatch &&
      roomMatch &&
      fromMatch &&
      toMatch &&
      statusMatch &&
      typeMatch &&
      tableMatch
    );
  });

  const totalOrders = filteredOrders.length;
  const totalRevenue = filteredOrders.reduce(
    (sum, o) => sum + (o.finalPrice || 0),
    0,
  );

  const stats = [
    { title: 'Total Orders', value: totalOrders, color: 'text.primary' },
    {
      title: 'Total Revenue',
      value: `${Companycurrency} ${totalRevenue.toFixed(2)}`,
      color: 'success.main',
    },
    {
      title: 'Pending Invoices',
      value: filteredOrders.filter((o) => !o.isInvoice).length,
      color: 'warning.main',
    },
    {
      title: 'Avg. Order Value',
      value: totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(0) : 0,
      color: 'text.primary',
    },
  ];

  const exportToExcel = () => {
    if (filteredOrders.length === 0) return;
    toast.info('Export functionality can be implemented here');
  };

  const handleViewDetail = async (order) => {
    setSelectedOrder(order);
    setOrderDetails([]);
    setDetailsDrawerOpen(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/GetOrderDetails/${order.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      setOrderDetails(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCloseDetailsDrawer = () => {
    setDetailsDrawerOpen(false);
    setSelectedOrder(null);
    setOrderDetails([]);
  };

  const handleOpenManage = (order) => {
    setSelectedOrder(order);
    setManageOpen(true);
  };
  const handleCloseManage = () => {
    setManageOpen(false);
    setSelectedOrder(null);
  };

  // Order action buttons, rendered once and reused. `closeFn` closes whichever
  // surface invoked them before running the action. (Caller guards selectedOrder.)
  const renderOrderActions = (closeFn) => (
    <>
      {canApplyDiscount && !selectedOrder.isPaid && (
        <Button
          fullWidth
          variant='outlined'
          color='secondary'
          startIcon={<LocalOfferIcon />}
          onClick={() => {
            closeFn();
            openDiscountModal(selectedOrder);
          }}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          Apply Discount
        </Button>
      )}

      {!selectedOrder.isPaid &&
        ((selectedOrder.bookingId || 0) > 0 ||
          !!(selectedOrder.roomNumbers || '').trim()) && (
          <Button
            fullWidth
            variant='outlined'
            color='warning'
            onClick={() => {
              closeFn();
              openChangeRoom(selectedOrder);
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            Change Room
          </Button>
        )}

      {canPrintBill && (
        <Button
          fullWidth
          variant='contained'
          color='primary'
          startIcon={<ReceiptIcon />}
          onClick={() => {
            closeFn();
            openPrintModal(selectedOrder);
          }}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          Print Bill
        </Button>
      )}
    </>
  );

  const getOrderStatusChip = (o) => {
    if (o.isPaid) return { label: 'Paid', color: 'success' };
    if (o.isInvoice) return { label: 'Invoiced', color: 'info' };
    return { label: 'Pending', color: 'warning' };
  };

  // ── Print Bill ────────────────────────────────────────────────
  const openPrintModal = (order) => {
    setCurrentOrderForAction(order);
    setCustomerName(order.firstName || '');
    setCustomerMobile(order.mobile || '');
    setCustomerAddress(order.address || '');
    setPrintModalOpen(true);
  };

  const handleContinueAsGuest = () => {
    setCustomerName('Guest');
    setCustomerMobile('0000000000');
    setCustomerAddress('Guest Address');
  };

  const handlePrintBill = async () => {
    debugger;
    if (!currentOrderForAction) return;

    const customerData = {
      name: customerName || 'Guest',
      mobile: customerMobile || '0000000000',
      address: customerAddress || 'Guest Address',
    };

    await sendToPrinterAPI(currentOrderForAction, customerData);

    setPrintModalOpen(false);
    setCustomerName('');
    setCustomerMobile('');
    setCustomerAddress('');
    fetchOrders();
  };

  const sendToPrinterAPI = async (order, customer) => {
    const machineId = window.location.hostname;
    const dateTime = new Date().toLocaleString('en-GB');

    let itemsForPrint = [];

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/GetOrderDetails/${order.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.ok) {
        const data = await res.json();
        itemsForPrint = Array.isArray(data) ? data : [];
      }
    } catch (err) {
      console.error('Error fetching items for print:', err);
    }

    if (itemsForPrint.length === 0) {
      toast.error('Items could not be loaded. Please try again.');
      return;
    }

    const header = 'Item         Qty  Price   Total \n';
    const itemsText = itemsForPrint
      .map((item) => {
        const qty = Number(item.qty || 1);
        const unitPrice = Number(item.price || item.finalPrice || 0);
        const totalPrice = unitPrice * qty;

        const name = (item.name || 'Item').substring(0, 13).padEnd(13, ' ');
        const qtyStr = String(qty).padEnd(4, ' ');
        const priceStr = unitPrice.toFixed(2).padStart(7, ' ');
        const totalStr = totalPrice.toFixed(2).padStart(8, ' ');

        return `${name}${qtyStr}${priceStr}${totalStr}`;
      })
      .join('\n');

    const divider = '--------------------------------\n';
    const subtotal = Number(order.price || 0);
    const grandTotal = Number(order.finalPrice || 0);
    const roundedTotal = Math.round(grandTotal);
    const roundOff = Number((roundedTotal - grandTotal).toFixed(2));

    const taxLines = `
CGST      : ${currency} ${Number(order.cGst || 0).toFixed(2)}
SGST      : ${currency} ${Number(order.sGst || 0).toFixed(2)}
Discount  : ${currency} ${Number(order.dicountamount || 0).toFixed(2)}
Round Off : ${roundOff >= 0 ? '+' : ''}${roundOff.toFixed(2)}
Total     : ${currency} ${roundedTotal.toFixed(2)}
`;

    const data =
      '\x1B\x40' +
      '\x1B\x61\x01' +
      (company?.name || 'HOTEL') +
      '\n' +
      (company?.address1 || '') +
      '\n' +
      divider +
      '\x1B\x45\x01BILL / INVOICE\n\x1B\x45\x00' +
      divider +
      '\x1B\x61\x00' +
      `Inv No : ORD_${order.id}\n` +
      `Room   : ${order.roomNumbers || '-'}\n` +
      `Date   : ${dateTime}\n` +
      divider +
      `Customer : ${customer.name}\n` +
      `Mobile   : ${customer.mobile}\n` +
      `Address  : ${customer.address || '-'}\n` +
      divider +
      `Items    : ${itemsForPrint.length}\n` +
      divider +
      header +
      divider +
      itemsText +
      '\n' +
      divider +
      `Subtotal : ${currency} ${subtotal.toFixed(2)}\n` +
      taxLines +
      divider +
      '\x1B\x61\x01***** THANK YOU *****\nPlease visit again!\n\n\n\n\n' +
      '\x1D\x56\x00';

    try {
      await fetch(`${config.API_BASE_URL}/Service/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'BILL',
          content: data,
          machineId: machineId,
          companyid: company?.id,
          orderId: order.id,
          customerName: customer.name,
          customerMobile: customer.mobile,
          printerName: resolveBillPrinter(),
        }),
      });

      toast.success('Bill sent to printer successfully!');
    } catch (err) {
      console.error('Print API error:', err);
      toast.error('Failed to send bill to printer');
    }
  };

  // ── Discount ──────────────────────────────────────────────────
  const openDiscountModal = (order) => {
    setCurrentOrderForAction(order);
    setDiscountAmount('');
    setDiscountModalOpen(true);
  };

  const handleSaveDiscount = async () => {
    if (!currentOrderForAction || !discountAmount) {
      toast.error('Please enter discount amount');
      return;
    }
    try {
      await fetch(`${config.API_BASE_URL}/home/AddDiscountForOrder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          orderId: currentOrderForAction.id,
          bookingId: currentOrderForAction.bookingId,
          discountAmount: parseFloat(discountAmount),
        }),
      });
      toast.success('Discount applied successfully!');
      setDiscountModalOpen(false);
      fetchOrders();
    } catch (err) {
      toast.error('Failed to apply discount');
      console.error(err);
    }
  };

  const clearFilters = () => {
    setSearchText('');
    setRoomFilter('');
    setFromDate('');
    setToDate('');
    setStatusFilter('All');
    setTypeFilter('All');
    setTableFilter('');
  };

  // ── Access Denied (ORDER_VIEW missing) ────────────────────────
  if (!canViewOrders) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}>
        <LockIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
        <Typography variant='h6' color='text.secondary' fontWeight={600}>
          Access Denied
        </Typography>
        <Typography variant='body2' color='text.disabled'>
          You don't have permission to view orders.
        </Typography>
      </Box>
    );
  }

  // ── Main Render ───────────────────────────────────────────────
  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 2, md: 3 },
        bgcolor: '#f8fafc',
        minHeight: '100vh',
        overflowX: 'hidden',
        maxWidth: '100%',
      }}>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        flexWrap='wrap'
        gap={1}
        mb={{ xs: 2, sm: 3 }}>
        <Typography
          variant='h5'
          fontWeight={700}
          color='primary'
          sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
          Order Management
        </Typography>
        <Button
          variant='outlined'
          size='small'
          startIcon={<RefreshIcon />}
          onClick={fetchOrders}
          disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2.5} mb={4}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent sx={{ py: 2.5, px: 3 }}>
                <Typography color='text.secondary' variant='body2' gutterBottom>
                  {stat.title}
                </Typography>
                <Typography
                  variant='h4'
                  fontWeight={600}
                  color={stat.color}
                  sx={{ fontSize: '1.75rem' }}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
        <Box
          display='flex'
          gap={{ xs: 1, sm: 2 }}
          flexWrap='wrap'
          alignItems={{ xs: 'stretch', sm: 'end' }}>
          <TextField
            label='Search Customer'
            size='small'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
          />
          <TextField
            label='Room Number'
            size='small'
            value={roomFilter}
            onChange={(e) => setRoomFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 160 } }}
          />
          <TextField
            type='date'
            label='From Date'
            size='small'
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 'unset' } }}
          />
          <TextField
            type='date'
            label='To Date'
            size='small'
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 'unset' } }}
          />
          <TextField
            select
            label='Payment'
            size='small'
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 130 } }}>
            <MenuItem value='All'>All</MenuItem>
            <MenuItem value='Paid'>Paid</MenuItem>
            <MenuItem value='Unpaid'>Unpaid</MenuItem>
          </TextField>
          <TextField
            select
            label='Order Type'
            size='small'
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 140 } }}>
            <MenuItem value='All'>All</MenuItem>
            <MenuItem value='Dine-in'>Dine-in</MenuItem>
            <MenuItem value='Takeaway'>Takeaway</MenuItem>
            <MenuItem value='Room'>Room</MenuItem>
          </TextField>
          <TextField
            label='Table No'
            size='small'
            value={tableFilter}
            onChange={(e) => setTableFilter(e.target.value)}
            sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 120 } }}
          />
          <Button
            variant='contained'
            size='small'
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            sx={{ flex: { xs: 1, sm: 'initial' } }}>
            Export CSV
          </Button>
          <Button
            variant='text'
            color='inherit'
            size='small'
            onClick={clearFilters}
            sx={{ flex: { xs: 1, sm: 'initial' } }}>
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Main Table — compact, horizontal scroll on mobile so 7-col layout stays readable */}
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: { xs: 'auto', md: 'hidden' } }}>
          <Table
            size='small'
            sx={{
              width: '100%',
              minWidth: { xs: 720, md: 'unset' },
              tableLayout: { xs: 'auto', md: 'fixed' },
              '& th, & td': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                px: { xs: 1, sm: 1.5 },
                fontSize: { xs: '0.72rem', sm: '0.875rem' },
              },
            }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    width: '11%',
                    textAlign: 'center',
                  }}>
                  Order ID
                </TableCell>
                <TableCell
                  sx={{ color: 'white', fontWeight: 600, width: '22%' }}>
                  Customer
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    width: '10%',
                    textAlign: 'center',
                  }}>
                  Room
                </TableCell>
                <TableCell
                  sx={{ color: 'white', fontWeight: 600, width: '18%' }}>
                  Time
                </TableCell>
                <TableCell
                  align='right'
                  sx={{ color: 'white', fontWeight: 600, width: '14%' }}>
                  Amount
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ color: 'white', fontWeight: 600, width: '12%' }}>
                  Status
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ color: 'white', fontWeight: 600, width: '13%' }}>
                  Details
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align='center' sx={{ py: 10 }}>
                    <CircularProgress size={45} />
                  </TableCell>
                </TableRow>
              ) : filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align='center' sx={{ py: 12 }}>
                    <Typography variant='h6' color='text.secondary'>
                      No orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((o) => {
                  const status = getOrderStatusChip(o);
                  return (
                    <TableRow key={o.id} hover>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>
                        ORD_{o.id}
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2' fontWeight={500} noWrap>
                          {o.firstName || 'Walk-in Customer'}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <Chip
                          label={o.roomNumbers ? roomLabel(o.roomNumbers) : '—'}
                          color='primary'
                          variant='filled'
                          size='small'
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2' noWrap>
                          {formatDate(o.createdate)}
                        </Typography>
                      </TableCell>
                      <TableCell align='right'>
                        <Typography
                          variant='body2'
                          fontWeight={700}
                          color='success.main'
                          noWrap>
                          {Companycurrency}{' '}
                          {Number(o.finalPrice || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <Chip
                          label={status.label}
                          color={status.color}
                          size='small'
                        />
                      </TableCell>
                      <TableCell align='center'>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                          }}>
                          <Button
                            size='small'
                            variant='outlined'
                            startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handleViewDetail(o)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: 11,
                              px: 1,
                              minWidth: 0,
                              whiteSpace: 'nowrap',
                            }}>
                            View More
                          </Button>
                          <Button
                            size='small'
                            variant='contained'
                            color='primary'
                            onClick={() => handleOpenManage(o)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: 11,
                              px: 1,
                              minWidth: 0,
                              whiteSpace: 'nowrap',
                              boxShadow: 'none',
                            }}>
                            Manage
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Print Bill Modal — only accessible when ORDER_PRINT_BILL is granted */}
      <Dialog
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        fullWidth
        maxWidth='sm'
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon color='primary' /> Print Bill - Customer Details
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText sx={{ mb: 3 }}>
            Please fill customer details for the bill.
          </DialogContentText>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                label='Customer Name'
                fullWidth
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Mobile Number'
                fullWidth
                value={customerMobile}
                onChange={(e) => setCustomerMobile(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Address (Optional)'
                fullWidth
                multiline
                rows={3}
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            flexWrap: 'wrap',
            gap: 1,
            '& > *': { flex: { xs: '1 1 100%', sm: '0 0 auto' } },
          }}>
          <Button onClick={handleContinueAsGuest} startIcon={<PersonIcon />}>
            Continue as Guest
          </Button>
          <Button onClick={() => setPrintModalOpen(false)}>Cancel</Button>
          <Button onClick={handlePrintBill} variant='contained' color='primary'>
            Submit & Print Bill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discount Modal */}
      <Dialog
        open={discountModalOpen}
        onClose={() => setDiscountModalOpen(false)}
        maxWidth='xs'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle>Add Discount</DialogTitle>
        <DialogContent>
          <TextField
            label='Discount Amount'
            type='number'
            fullWidth
            sx={{ mt: 2 }}
            value={discountAmount}
            onChange={(e) => setDiscountAmount(e.target.value)}
            InputProps={{ startAdornment: <Typography>₹</Typography> }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscountModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveDiscount}
            variant='contained'
            color='warning'>
            Apply Discount
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Details Drawer */}
      <Drawer
        anchor='right'
        open={detailsDrawerOpen && !!selectedOrder}
        onClose={handleCloseDetailsDrawer}
        transitionDuration={280}
        sx={{ zIndex: ORDER_DRAWER_Z }}
        ModalProps={{ sx: { zIndex: ORDER_DRAWER_Z } }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.35)',
              backdropFilter: 'blur(2px)',
            },
          },
        }}
        PaperProps={{
          sx: {
            width: ORDER_DRAWER_WIDTH,
            maxWidth: '100vw',
            bgcolor: drawerTheme.bg,
            borderLeft: `1px solid ${drawerTheme.border}`,
            borderTopLeftRadius: '20px',
            borderBottomLeftRadius: '20px',
            boxShadow: '-8px 0 30px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}>
        {selectedOrder && (
          <>
            <Box
              sx={{
                flexShrink: 0,
                bgcolor: drawerTheme.primary,
                color: '#fff',
                px: 2,
                pt: 1.75,
                pb: 1.5,
                borderTopLeftRadius: '20px',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                }}>
                <Box minWidth={0}>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>
                    Order Details
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      color: 'rgba(255,255,255,0.88)',
                      mt: 0.35,
                    }}>
                    ORD_{selectedOrder.id} · Room{' '}
                    {selectedOrder.roomNumbers ? roomLabel(selectedOrder.roomNumbers) : '—'}
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleCloseDetailsDrawer}
                  size='small'
                  sx={{
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.14)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
                  }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 2,
                py: 1.75,
              }}>
              <DrawerSection title='Order Overview'>
                <DrawerRow
                  label='Customer'
                  value={selectedOrder.firstName || 'Walk-in Customer'}
                />
                {selectedOrder.email && (
                  <DrawerRow label='Email' value={selectedOrder.email} />
                )}
                {selectedOrder.mobile && (
                  <DrawerRow label='Mobile' value={selectedOrder.mobile} />
                )}
                <DrawerRow
                  label='Room'
                  value={selectedOrder.roomNumbers ? roomLabel(selectedOrder.roomNumbers) : '—'}
                />
                <DrawerRow
                  label='Date & Time'
                  value={formatDate(selectedOrder.createdate)}
                />
                <DrawerRow
                  label='Final Amount'
                  value={`${Companycurrency} ${(
                    (orderDetails || []).reduce(
                      (s, it) => s + Number(it.totalPrice || it.finalPrice || 0),
                      0,
                    ) || Number(selectedOrder.finalPrice || 0)
                  ).toFixed(2)}`}
                  highlight
                  isLast
                />
              </DrawerSection>

              <DrawerSection title='Ordered Items'>
                {orderDetails.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  orderDetails.map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: 1.75,
                        py: 1.1,
                        borderBottom:
                          i < orderDetails.length - 1
                            ? `1px solid ${drawerTheme.divider}`
                            : 'none',
                      }}>
                      <Box minWidth={0}>
                        <Typography
                          sx={{ fontSize: 13, fontWeight: 600 }}
                          noWrap>
                          {item.name}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 11, color: drawerTheme.textMuted }}>
                          Qty {item.qty}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                        {Companycurrency}{' '}
                        {Number(
                          item.totalPrice || item.finalPrice || 0,
                        ).toFixed(2)}
                      </Typography>
                    </Box>
                  ))
                )}
              </DrawerSection>

              {/* Tax Summary for Room Orders — same inclusive-GST extraction
                  used by Table Orders so values stay consistent across
                  screens. Sums per-line GST when present; otherwise derives
                  from finalPrice − finalPrice/(1+rate/100). */}
              {(() => {
                // Grand Total MUST equal the sum of the line items shown above
                // (same value per line). The stored order.finalPrice drifted
                // out of sync on orders edited before the duplicate-line fix, so
                // the summary showed a smaller total than the items added up to.
                const lineSum = (orderDetails || []).reduce(
                  (s, it) => s + Number(it.totalPrice || it.finalPrice || 0),
                  0,
                );
                const total =
                  lineSum > 0 ? lineSum : Number(selectedOrder?.finalPrice || 0);
                if (total <= 0) return null;
                const rate = Number(company?.gstRate ?? company?.gstrate ?? 5);
                const perLineGst = (orderDetails || []).reduce(
                  (s, it) => s + Number(it.gstAmount || 0),
                  0,
                );
                const perLineCgst = (orderDetails || []).reduce(
                  (s, it) => s + Number(it.cgst || it.cGst || 0),
                  0,
                );
                const perLineSgst = (orderDetails || []).reduce(
                  (s, it) => s + Number(it.sgst || it.sGst || 0),
                  0,
                );
                const derivedGst =
                  perLineGst > 0
                    ? perLineGst
                    : Number((total - total / (1 + rate / 100)).toFixed(2));
                const derivedHalf = Number((derivedGst / 2).toFixed(2));
                const cgst = perLineCgst > 0 ? perLineCgst : derivedHalf;
                const sgst = perLineSgst > 0 ? perLineSgst : derivedHalf;
                const subtotalBase = Number((total - derivedGst).toFixed(2));
                const discount = Number(selectedOrder?.discount || 0);
                const paid = Number(selectedOrder?.paidAmount || 0);
                const due = Number(selectedOrder?.pendingAmount || 0);
                return (
                  <DrawerSection title='Tax & Payment Summary'>
                    <DrawerRow
                      label='Subtotal (excl. GST)'
                      value={`${Companycurrency} ${subtotalBase.toFixed(2)}`}
                    />
                    <DrawerRow
                      label={`CGST (${(rate / 2).toFixed(2)}%)`}
                      value={`${Companycurrency} ${cgst.toFixed(2)}`}
                    />
                    <DrawerRow
                      label={`SGST (${(rate / 2).toFixed(2)}%)`}
                      value={`${Companycurrency} ${sgst.toFixed(2)}`}
                    />
                    <DrawerRow
                      label='GST Amount (incl.)'
                      value={`${Companycurrency} ${derivedGst.toFixed(2)}`}
                    />
                    {discount > 0 && (
                      <DrawerRow
                        label='Discount'
                        value={`${Companycurrency} ${discount.toFixed(2)}`}
                      />
                    )}
                    <DrawerRow
                      label='Grand Total'
                      value={`${Companycurrency} ${total.toFixed(2)}`}
                      highlight
                    />
                    {paid > 0 && (
                      <DrawerRow
                        label='Paid'
                        value={`${Companycurrency} ${paid.toFixed(2)}`}
                      />
                    )}
                    {due > 0 && (
                      <DrawerRow
                        label='Due'
                        value={`${Companycurrency} ${due.toFixed(2)}`}
                      />
                    )}
                  </DrawerSection>
                );
              })()}
            </Box>

            {/* Action buttons moved to the "Manage" dialog (opened from the
                orders table) — the details drawer is now detail-only. */}
          </>
        )}
      </Drawer>

      {/* Manage Order — actions dialog opened from the orders table. */}
      <Dialog
        open={manageOpen && !!selectedOrder}
        onClose={handleCloseManage}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Manage Order
          {selectedOrder ? ` — ORD_${selectedOrder.id}` : ''}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 2 }}>
          {selectedOrder && renderOrderActions(handleCloseManage)}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManage}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Change Room dialog — pick the correct active-booking room */}
      <Dialog
        open={changeRoomOpen}
        onClose={() => setChangeRoomOpen(false)}
        maxWidth='xs'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Change Room</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>
            Move bill <b>ORD_{orderForRoomChange?.id}</b>
            {orderForRoomChange?.roomNumbers
              ? ` (currently Room ${orderForRoomChange.roomNumbers})`
              : ''}{' '}
            to the correct room.
          </DialogContentText>
          <TextField
            select
            fullWidth
            label='Correct Room (active bookings)'
            value={roomTarget}
            onChange={(e) => setRoomTarget(e.target.value)}>
            {activeRooms.length === 0 && (
              <MenuItem value='' disabled>
                No active room bookings
              </MenuItem>
            )}
            {activeRooms.map((r) => (
              <MenuItem key={r.room} value={r.room}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setChangeRoomOpen(false)} color='inherit'>
            Cancel
          </Button>
          <Button
            onClick={submitChangeRoom}
            variant='contained'
            color='warning'
            disabled={savingRoom || !roomTarget}>
            {savingRoom ? 'Moving…' : 'Move Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
